import { useEffect, useState } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { Badge, Button, Card, PageLoader, Typography } from '../ui';
import { AvatarPhotoEditor } from '../AvatarPhotoEditor';
import { setContactAvatar, useContact, type WebContact, type WebOutreachThread } from '../../../lib/contact-book';
import {
  groupThreads,
  longRelativeTime,
  sourceLabelKey,
  threadPreview,
  threadTitle,
} from '../../../lib/contact-rows';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { chatHandoffLink } from '../../../lib/chat-handoff';
import { useBasePath } from '../../../lib/use-base-path';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useTranslation } from '../../../lib/i18n';
import { useContactSeen } from '../../../lib/use-contact-seen';
import { AddChannelRows } from './AddChannelRows';
import { ChannelChip } from './ChannelChip';
import styles from './contacts.module.css';

/**
 * One contact.
 *
 * Reads the same endpoint as the list, filtered to this person, so a deep link works without the
 * list ever having been open. Everything on the screen arrives in that one response — there is no
 * second call for facts, channels or threads.
 */
export function ContactDetailBody({ personId }: { personId: string }) {
  const { t, i18n } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const entry = useContact(personId);
  // Opening the correspondence is what makes it seen — stamped once the person's data is on screen, so a
  // loader that never resolves does not clear a dot the reader never got to read.
  const seen = useContactSeen();
  const seenMark = seen.mark;
  const [imageError, setImageError] = useState<string | null>(null);
  useEffect(() => {
    if (entry) seenMark(personId);
  }, [entry, personId, seenMark]);

  if (!entry) return <PageLoader />;

  const reachOutHref = chatHandoffLink(
    {
      kind: 'new',
      message: t('contacts.detail.reachOutSeed', {
        name: entry.contact?.displayName ?? '',
      }),
    },
    basePath,
  ).route;

  const card = entry.contact;
  // Grouped rather than one flat history, because an outreach carries no title to head it with —
  // its status, its channel and its dates are the only things a group header can honestly say.
  const { active, done } = groupThreads([...(entry.outbound ?? []), ...(entry.inbound ?? [])]) as {
    active: WebOutreachThread[];
    done: WebOutreachThread[];
  };

  return (
    <div className={styles.detail}>
      <div className={styles.detailHeader}>
        <AvatarPhotoEditor
          initials={avatarInitials(card?.displayName ?? '')}
          photoUrl={card?.effectiveAvatarUrl}
          size={48}
          // Same seed as the list row, so a contact keeps one colour from the list into the detail.
          color={avatarSlotFor(personId || card?.displayName || '')}
          // Offer Remove whenever a photo shows: clearing writes an empty override, which drops back to
          // the server-resolved photo (the contact's own self-photo or the CRM-derived one). The card
          // only carries the resolved value, so this is the honest signal available here.
          canRemove={!!card?.effectiveAvatarUrl}
          onSave={async (url) => {
            const ok = await setContactAvatar(personId, url);
            if (!ok) throw new Error('contact avatar save failed');
          }}
          onError={setImageError}
          page="contact_detail"
          buttonName="edit_contact_photo"
        />
        {imageError && <p className={styles.imageError}>{imageError}</p>}
        <Typography variant="h2" className={styles.detailName}>{card?.displayName}</Typography>
        {card?.labels?.length ? (
          <div className={styles.labels}>
            {card.labels.map((label) => (
              <span key={label} className={styles.label}>
                <Typography variant="p3" color="secondary">
                  {label}
                </Typography>
              </span>
            ))}
          </div>
        ) : null}
        <div className={styles.detailActions}>
          {/* Enabled only when there is a recipient to reach: a person in the book the user has
              never messaged has none, and the button would have nothing to address. */}
          {/* Not a direct send: reaching out is the assistant's job, so this seeds a fresh chat and
              lets the Maestro compose and confirm — the same handoff "Remove agent" uses, rather
              than a second, silent outreach path that bypasses the conversation. */}
          {/* `href` renders a real <a>, and an anchor cannot be disabled — cmd-click would follow
              it regardless. So the unreachable case is a plain disabled Button instead. */}
          {entry.recipientId ? (
            <Button
              href={reachOutHref}
              onClick={() =>
                logPageClickBi({ page_name: 'contact_detail', button_name: 'reach_out' })
              }
            >
              {t('contacts.detail.reachOut')}
            </Button>
          ) : (
            <Button disabled>
              {t('contacts.detail.reachOut')}
            </Button>
          )}
        </div>
      </div>

      <Section title={t('contacts.detail.channels')}>
        {(entry.channels ?? []).map((channel) => (
            <div key={`${channel.kind}-${channel.value}`} className={styles.sectionRow}>
              <span className={styles.threadMain}>
                <Typography variant="p2">{channel.label}</Typography>
                <Typography variant="p3" color="secondary">
                  {channel.value}
                </Typography>
              </span>
              {channel.preferred ? (
                <Badge variant="connected" size="small" label={t('contacts.detail.preferred')} />
              ) : null}
            </div>
          ))}
          {/* A way to fill in what is missing, per kind. The section renders even with no channels
              at all, which is the case that most needs it — a contact imported from the phone book
              with only a number, or a person the agent learned about with neither. */}
          <AddChannelRows personId={personId} channels={entry.channels ?? []} />
        </Section>

      {entry.facts?.length ? (
        <Section title={t('contacts.detail.knows')}>
          {entry.facts.map((fact) => (
            <div key={fact} className={styles.sectionRow}>
              <Typography variant="p2">{fact}</Typography>
            </div>
          ))}
        </Section>
      ) : null}

      {entry.openLoops?.length ? (
        <Section title={t('contacts.detail.openLoops')}>
          {entry.openLoops.map((loop) => (
            <div key={loop} className={styles.sectionRow}>
              <Typography variant="p2" color="accent">
                {loop}
              </Typography>
            </div>
          ))}
        </Section>
      ) : null}

      {active.length ? (
        <Section title={t('contacts.detail.threadsActive', { count: active.length })}>
          {active.map((thread) => (
            <ThreadRow key={thread.outreachId} thread={thread} personId={personId} />
          ))}
        </Section>
      ) : null}

      {done.length ? (
        <Section title={t('contacts.detail.threadsDone', { count: done.length })}>
          {done.map((thread) => (
            <ThreadRow key={thread.outreachId} thread={thread} personId={personId} />
          ))}
        </Section>
      ) : null}

      <Section title={t('contacts.detail.about')}>
        {entry.source ? (
          <MetaRow
            label={t('contacts.detail.source')}
            // An unrecognised source renders as the server sent it, rather than as a blank row.
            value={sourceLabelKey(entry.source) ? t(sourceLabelKey(entry.source)!) : entry.source}
          />
        ) : null}
        {card?.lastTouchAt ? (
          <MetaRow
            label={t('contacts.detail.lastContacted')}
            value={longRelativeTime(card.lastTouchAt)}
          />
        ) : null}
        {entry.cadenceDays ? (
          <MetaRow
            label={t('contacts.detail.cadence')}
            // Built as a template literal, the form the source-scan guard skips — this is the one
            // key here that genuinely inflects ("Every day" is not "Every 1 days").
            value={t(`contacts.detail.cadenceDays.${entry.cadenceDays === 1 ? 'one' : 'other'}`, {
              count: entry.cadenceDays,
            })}
          />
        ) : null}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <Typography variant="p3" color="secondary">
        {title}
      </Typography>
      <Card variant="outline" size="sm">
        <div className={styles.sectionRows}>{children}</div>
      </Card>
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.sectionRow}>
      <Typography variant="p2" color="secondary">
        {label}
      </Typography>
      <Typography variant="p2">{value}</Typography>
    </div>
  );
}

/**
 * One outreach, as a row that opens it.
 *
 * A row rather than an expander: the real messages run to thousands of characters of Markdown, and
 * a conversation read inside a card in a list of cards cannot be followed. The thread gets its own
 * screen; this row's job is only to say which outreach it is.
 *
 * Which is harder than it sounds, because an outreach has no subject and one cannot be derived —
 * every body opens with the same greeting and assistant boilerplate. So the row leans on what does
 * differ: the channel, the status, when it opened, and the latest thing said on it.
 */
function ThreadRow({ thread, personId }: { thread: WebOutreachThread; personId: string }) {
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const basePath = useBasePath();
  return (
    // The Card is the surface, the Link is the hit target — `CardProps` extends HTMLAttributes and
    // carries no href, so the anchor wraps it rather than the Card pretending to be one.
    <Link
      href={`${basePath}/contacts/${personId}/${thread.outreachId}`}
      className={styles.threadLink}
      onClick={() =>
        logPageClickBi({ page_name: 'contact_detail', button_name: 'open_outreach_thread' })
      }
    >
    <Card variant="outline" size="sm" interactive className={styles.threadHead}>
      <span className={styles.threadMain}>
        {/* The outreach's real subject leads, with the channel beside it — the medium is part of
            what identifies a thread, and a grey label under the title did not read as one. */}
        <span className={styles.threadTitleRow}>
          <Typography variant="h4" className={styles.threadTitleText}>
            {threadTitle(thread, i18n.language)}
          </Typography>
          <ChannelChip channel={thread.channel} />
        </span>
        {thread.status ? (
          <span className={styles.threadHeadTop}>
            <Badge
              variant={thread.status === 'completed' ? 'done' : 'in-progress'}
              size="tiny"
              label={t(`contacts.detail.status.${thread.status}`)}
            />
          </span>
        ) : null}
        <Typography variant="p3" color="secondary" className={styles.threadText}>
          {threadPreview(thread)}
        </Typography>
      </span>
      <ChevronRight size={16} aria-hidden />
    </Card>
    </Link>
  );
}

export type { WebContact };
